/**
 * Mail attachments on the web: downloads go through the bearer-authenticated route (no file token for mail), and
 * uploads for an email are plain uploads with no conversation or page (purpose "mail"), sent by file id.
 */
import type { CreateUploadRequest } from "@bundle/contracts";
import type { PickedFile } from "@bundle/core";
import { saveBlob, type WebApi } from "./api.ts";
import { reportBillingFault } from "./billingFaults.ts";
import { desktopOpenDownload, desktopRevealDownload, desktopSaveDownload, isDesktop } from "./desktop.ts";
import { blankTab, openMailAttachment, type AttachmentEnv, type AttachmentIntent, type AttachmentOutcome } from "./mailAttachments.ts";

type Attachment = { id: string; filename: string; contentType?: string; size?: number };

/** Mail's `openAttachment` / `revealAttachment` for this page: the desktop shell's downloads inside the app, else the browser's. */
export function mailAttachmentOpener(api: WebApi, ws: string): {
  openAttachment: (messageId: string, a: Attachment, intent: AttachmentIntent, onProgress?: (fraction: number) => void) => Promise<AttachmentOutcome>;
  revealAttachment?: (path: string) => void;
} {
  const desktop = isDesktop();
  const openAttachment = (messageId: string, a: Attachment, intent: AttachmentIntent, onProgress?: (fraction: number) => void) => {
    const env: AttachmentEnv = {
      ...(desktop ? { desktop: { save: desktopSaveDownload, open: desktopOpenDownload } } : {}),
      openTab: () => blankTab(a.filename),
      saveBlob,
      objectUrl: (blob) => URL.createObjectURL(blob),
      revokeUrl: (url) => URL.revokeObjectURL(url),
    };
    return openMailAttachment(env, intent, a, () =>
      api.fetchMailAttachment(ws, messageId, a.id, (loaded, total) => {
        const of = total ?? a.size;
        if (of !== undefined && of > 0) onProgress?.(Math.min(1, loaded / of));
      }),
    );
  };
  return desktop ? { openAttachment, revealAttachment: (path) => void desktopRevealDownload(path) } : { openAttachment };
}

export async function uploadMailAttachment(api: WebApi, ws: string, f: PickedFile): Promise<string> {
  // contracts' CreateUploadRequest has no "mail" purpose yet (see the report); the API's claimMailFiles needs files with
  // neither channelId nor pageId, which only a purpose allows.
  const req = { name: f.name, mime: f.mime || "application/octet-stream", size: f.size, purpose: "mail" } satisfies CreateUploadRequest;
  const r = await api.createUpload(ws, req).catch((e: unknown) => {
    reportBillingFault(ws, e);
    throw e;
  });
  const blob = f.source as Blob;
  if (r.upload.mode === "single") {
    await api.uploadContent(ws, r.file.id, blob);
    return r.file.id;
  }
  const size = r.upload.partSize ?? blob.size;
  const count = r.upload.partCount ?? 1;
  for (let n = 1; n <= count; n++) {
    if (r.upload.uploadedParts?.includes(n)) continue;
    await api.uploadPart(ws, r.file.id, n, blob.slice((n - 1) * size, n * size));
  }
  await api.completeUpload(ws, r.file.id);
  return r.file.id;
}
